import { EUROPEAN_UNION_SHORTCUT } from "@redotech/locale/countries";
import { COUNTRY_CODES } from "@redotech/locale/country-codes";
import { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import { TagKind } from "@redotech/redo-common-model-tags-common/tag";
import { ShipmentTrackerStatus } from "@redotech/redo-fulfillment-group-common/outbound-shipment-label";
import { t } from "@redotech/redo-i18n";
import BankNote02 from "@redotech/redo-icons/bank-note-02.svg";
import Building02Icon from "@redotech/redo-icons/building-02.svg";
import CalendarIcon from "@redotech/redo-icons/calendar.svg";
import CheckVerified01Icon from "@redotech/redo-icons/check-verified-01.svg";
import Globe02Icon from "@redotech/redo-icons/globe-02.svg";
import MailIcon from "@redotech/redo-icons/mail-01.svg";
import MarkerPinIcon from "@redotech/redo-icons/marker-pin-01.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-cw-01.svg";
import StatusIcon from "@redotech/redo-icons/status.svg";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import WrenchIcon from "@redotech/redo-icons/tool-02.svg";
import Truck01Icon from "@redotech/redo-icons/truck-01.svg";
import type { RedoManagedReturnsRpcClient } from "@redotech/redo-merchant-managed-returns-rpc/client";
import {
  DropoffStatus,
  dropoffStatusToDisplayText,
} from "@redotech/redo-return-core-common/dropoff/dropoff-status";
import {
  ExchangePaymentStatus,
  returnTableFilterData,
  ReturnTableFilterNames,
} from "@redotech/redo-return-core-common/return-search-filters";
import {
  ReturnStatus,
  returnStatusToDisplayText,
} from "@redotech/redo-return-core-common/return-status";
import {
  ArrayStringFilterOperator,
  type ArrayStringFilterData,
} from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { BooleanFilterOperator } from "@redotech/redo-views-common/advanced-filters/boolean-filter";
import { DateFilterOperator } from "@redotech/redo-views-common/advanced-filters/date-filter";
import { AdvancedFilterType } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { NumberFilterOperator } from "@redotech/redo-views-common/advanced-filters/number-filter";
import {
  AdvancedTableFilter,
  ArrayStringTableFilter,
  BooleanTableFilter,
} from "@redotech/redo-web/advanced-filters/advanced-filter";
import { formatSentenceCase } from "@redotech/util/string";
import { shippingStatusToBadgeColor } from "./return-page-right-panel/shipping-status-badge";
import {
  getReturnListDropoffStatusLabel,
  getReturnListShipmentTrackerLabel,
  getReturnListStatusLabel,
  getReturnListTypeLabel,
} from "./returns-list-labels";
import { returnStatusToBadgeColor } from "./util";

/**
 * The add-filter dropdown renders fields in array order, so every returns table
 * sorts its filter list through this before handing it to the table.
 */
export function sortAdvancedFiltersByDisplayName<
  T extends { displayName: string },
>(filters: T[], locale?: string): T[] {
  return [...filters].sort((a, b) =>
    a.displayName.localeCompare(b.displayName, locale, { sensitivity: "base" }),
  );
}

const ReturnTypeLabels: { [key: string]: string } = {
  refund: "Refund",
  exchange: "Exchange",
  store_credit: "Store Credit",
};

const returnTypes = ["exchange", "refund", "store_credit"];

// Advanced filter implementation for date range
export const DateRangeFilter: AdvancedTableFilter = {
  type: AdvancedFilterType.DATE,
  Icon: CalendarIcon,
  operators: [
    DateFilterOperator.WITHIN,
    DateFilterOperator.BEFORE,
    DateFilterOperator.AFTER,
  ],
  data: returnTableFilterData[ReturnTableFilterNames.CREATED_AT],
  displayName: "Return created date",
};

// Advanced filter implementation for processed date range
export const ProcessedDateRangeFilter: AdvancedTableFilter = {
  type: AdvancedFilterType.DATE,
  Icon: CalendarIcon,
  operators: [
    DateFilterOperator.WITHIN,
    DateFilterOperator.BEFORE,
    DateFilterOperator.AFTER,
  ],
  data: returnTableFilterData[ReturnTableFilterNames.PROCESSED_AT],
  displayName: "Return processed date",
};

// Advanced filter implementation for return status
export const ReturnStatusAdvancedFilter: ArrayStringTableFilter<ReturnStatus> =
  {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: StatusIcon,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    data: returnTableFilterData[ReturnTableFilterNames.STATUS],
    itemLabel: "status",
    itemLabelPlural: "statuses",
    values: Object.values(ReturnStatus).filter(
      (status) => status !== ReturnStatus.PRE_SHIPMENT,
    ),
    display: (item: ReturnStatus) => returnStatusToDisplayText[item] || item,
    badgeColor: (item: ReturnStatus) => returnStatusToBadgeColor[item],
    displayName: "Status",
  };

export function createZipCodeFilterData(
  zipCode: string,
): ArrayStringFilterData {
  return {
    ...returnTableFilterData[ReturnTableFilterNames.ZIP_CODE],
    value: [zipCode.trim()],
    operator: ArrayStringFilterOperator.STARTS_WITH,
  };
}

export const ZipCodeAdvancedFilter: ArrayStringTableFilter<string> = {
  type: AdvancedFilterType.ARRAY_TO_STRING,
  Icon: MarkerPinIcon,
  operators: [
    ArrayStringFilterOperator.CONTAINS,
    ArrayStringFilterOperator.NOT_CONTAINS,
    ArrayStringFilterOperator.STARTS_WITH,
    ArrayStringFilterOperator.DOES_NOT_START_WITH,
    ArrayStringFilterOperator.IS_EMPTY,
    ArrayStringFilterOperator.IS_NOT_EMPTY,
  ],
  data: returnTableFilterData[ReturnTableFilterNames.ZIP_CODE],
  itemLabel: "Order ZIP",
  itemLabelPlural: "Order ZIPs",
  displayName: "Order ZIP",
  emptyValueText: "Enter ZIP",
};

/**
 * Product SKU values are unbounded and match when any return item satisfies
 * the selected operator.
 */
export const ProductSkuAdvancedFilter: ArrayStringTableFilter<string> = {
  type: AdvancedFilterType.ARRAY_TO_STRING,
  Icon: PackageIcon,
  operators: [
    ArrayStringFilterOperator.CONTAINS,
    ArrayStringFilterOperator.NOT_CONTAINS,
    ArrayStringFilterOperator.STARTS_WITH,
    ArrayStringFilterOperator.DOES_NOT_START_WITH,
  ],
  data: returnTableFilterData[ReturnTableFilterNames.PRODUCT_SKU],
  itemLabel: "SKU",
  itemLabelPlural: "SKUs",
  displayName: "Product SKU",
  emptyValueText: "Enter SKU",
};

// Advanced filter implementation for return type
export const ReturnTypeAdvancedFilter: ArrayStringTableFilter<string> = {
  type: AdvancedFilterType.ARRAY_TO_STRING,
  Icon: TagIcon,
  operators: [
    ArrayStringFilterOperator.ANY_OF,
    ArrayStringFilterOperator.ALL_OF,
  ],
  data: returnTableFilterData[ReturnTableFilterNames.RETURN_TYPE],
  itemLabel: "type",
  itemLabelPlural: "types",
  values: returnTypes,
  display: (item: string) => ReturnTypeLabels[item] || item,
  displayName: "Compensation method",
};

// Advanced filter implementation for exchange payment status
export const ExchangePaymentStatusAdvancedFilter: ArrayStringTableFilter<ExchangePaymentStatus> =
  {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: BankNote02,
    operators: [ArrayStringFilterOperator.ANY_OF],
    data: returnTableFilterData[ReturnTableFilterNames.EXCHANGE_PAYMENT_STATUS],
    itemLabel: "payment status",
    itemLabelPlural: "payment statuses",
    values: Object.values(ExchangePaymentStatus),
    display: (item: string) =>
      item === ExchangePaymentStatus.PENDING ? "Pending" : "Complete",
    displayName: "Exchange payment status",
    useSingleSelect: true,
  };

export const DispositionCompletedFilter: BooleanTableFilter = {
  type: AdvancedFilterType.BOOLEAN,
  Icon: CheckVerified01Icon,
  operators: [BooleanFilterOperator.IS],
  data: returnTableFilterData[
    ReturnTableFilterNames.ALL_PRODUCTS_DISPOSITION_COMPLETED
  ],
  boolDisplay: { trueDisplay: "true", falseDisplay: "false" },
  displayName: "Awaiting disposition",
};

// Tag filter that fetches team tags dynamically
export function createTagsAdvancedFilter(
  rpcMerchantClient: RedoMerchantRpcClient,
): ArrayStringTableFilter<string> {
  return {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: TagIcon,
    operators: [
      ArrayStringFilterOperator.ALL_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    data: returnTableFilterData[ReturnTableFilterNames.TAGS],
    itemLabel: "tag",
    itemLabelPlural: "tags",
    displayName: "Tags",
    dbSearch: true,
    valueFetcher: async ({ filterSearch }, signal) => {
      try {
        const { tags } = await rpcMerchantClient.getTeamTags(
          { kind: TagKind.RETURN },
          { signal },
        );

        let tagNames = tags.map((tag) => tag.name);

        // Filter by search string if provided
        if (filterSearch) {
          tagNames = tagNames.filter((tagName) =>
            tagName.toLowerCase().includes(filterSearch.toLowerCase()),
          );
        }

        // Limit to 50 items for performance
        return tagNames.slice(0, 50);
      } catch (error) {
        console.error("Failed to fetch team tags:", error);
        return [];
      }
    },
    display: (item: string) => item,
  };
}

export const ReviewingPartyFilter: BooleanTableFilter = {
  type: AdvancedFilterType.BOOLEAN,
  Icon: CheckVerified01Icon,
  operators: [BooleanFilterOperator.IS],
  data: returnTableFilterData[ReturnTableFilterNames.REVIEWING_PARTY],
  boolDisplay: { trueDisplay: "Merchant review", falseDisplay: "Redo managed" },
  displayName: "Reviewing party",
};

export const WarrantyRepairFilter: BooleanTableFilter = {
  type: AdvancedFilterType.BOOLEAN,
  Icon: WrenchIcon,
  operators: [BooleanFilterOperator.IS],
  data: {
    name: "isRepair",
    type: AdvancedFilterType.BOOLEAN,
    value: null,
    operator: BooleanFilterOperator.IS,
  },
  boolDisplay: { trueDisplay: "true", falseDisplay: "false" },
  displayName: "Is repair",
};

export function createRecycleListAdvancedFilters({
  language,
  merchantRpcClient,
}: {
  language: string;
  merchantRpcClient: RedoMerchantRpcClient;
}): AdvancedTableFilter[] {
  return sortAdvancedFiltersByDisplayName(
    [
      { ...DateRangeFilter, displayName: t("Return created date") },
      {
        ...ReturnStatusAdvancedFilter,
        display: getReturnListStatusLabel,
        displayName: t("Status"),
        itemLabel: t("status", { context: "filter value noun" }),
        itemLabelPlural: t("statuses", { context: "filter value noun" }),
      },
      {
        ...PartiallyProcessedFilter,
        boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
        displayName: t("Partially processed"),
      },
      {
        ...FailedRmaCreationFilter,
        boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
        displayName: t("Failed RMA creation", { context: "RMA acronym" }),
      },
      {
        ...createTagsAdvancedFilter(merchantRpcClient),
        displayName: t("Tags"),
        itemLabel: t("tag", { context: "filter value noun" }),
        itemLabelPlural: t("tags", { context: "filter value noun" }),
      },
    ],
    language,
  );
}

// Advanced filter implementation for shipment status
export const ShipmentStatusAdvancedFilter: ArrayStringTableFilter<ShipmentTrackerStatus> =
  {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: Truck01Icon,
    operators: [ArrayStringFilterOperator.ANY_OF],
    data: returnTableFilterData[ReturnTableFilterNames.SHIPMENT_STATUS],
    itemLabel: "shipment status",
    itemLabelPlural: "shipment statuses",
    // We want to exclude unknown because we are combining it with pre_transit
    // EasyPost confirmed that "unknown" and "pre_transit" are interchangeable and can be treated as the same
    values: Object.values(ShipmentTrackerStatus).filter(
      (status) => status !== ShipmentTrackerStatus.UNKNOWN,
    ),
    display: (item: ShipmentTrackerStatus) => {
      return formatSentenceCase(item);
    },
    badgeColor: shippingStatusToBadgeColor,
    displayName: "Shipment status",
  };

// Custom filter for shipment days since last update
// This filter will use the number filter infrastructure but convert to date ranges in the backend
export const ShipmentDaysSinceLastUpdateFilter: AdvancedTableFilter = {
  type: AdvancedFilterType.NUMBER,
  Icon: CalendarIcon,
  operators: [NumberFilterOperator.GREATER_THAN],
  min: 0,
  data: {
    name: ReturnTableFilterNames.SHIPMENT_DAYS_SINCE_LAST_UPDATE,
    type: AdvancedFilterType.NUMBER,
    value: null,
    operator: NumberFilterOperator.GREATER_THAN,
  },
  displayName: "Shipment days since last update",
};

export const PartiallyProcessedFilter: BooleanTableFilter = {
  type: AdvancedFilterType.BOOLEAN,
  Icon: CheckVerified01Icon,
  operators: [BooleanFilterOperator.IS],
  data: returnTableFilterData[ReturnTableFilterNames.PARTIALLY_PROCESSED],
  boolDisplay: { trueDisplay: "true", falseDisplay: "false" },
  displayName: "Partially processed",
};

export const AwaitingRemitFilter: BooleanTableFilter = {
  type: AdvancedFilterType.BOOLEAN,
  Icon: BankNote02,
  operators: [BooleanFilterOperator.IS],
  data: returnTableFilterData[ReturnTableFilterNames.AWAITING_REMIT],
  boolDisplay: { trueDisplay: "true", falseDisplay: "false" },
  displayName: "Awaiting remit",
};

/**
 * Matches any needs-action view of the table's type. Reached from the home
 * page's links, never from the add-filter picker.
 */
export const NeedsActionFilter: BooleanTableFilter = {
  type: AdvancedFilterType.BOOLEAN,
  Icon: StatusIcon,
  operators: [BooleanFilterOperator.IS],
  data: returnTableFilterData[ReturnTableFilterNames.NEEDS_ACTION],
  boolDisplay: { trueDisplay: "true", falseDisplay: "false" },
  displayName: "Needs action",
  hiddenFromPicker: true,
};

/**
 * One customer's records, matched on the exact email. Reached from the home
 * page's customer rows, never from the add-filter picker.
 */
const CustomerEmailFilter: ArrayStringTableFilter<string> = {
  type: AdvancedFilterType.ARRAY_TO_STRING,
  Icon: MailIcon,
  operators: [ArrayStringFilterOperator.ANY_OF],
  data: returnTableFilterData[ReturnTableFilterNames.CUSTOMER_EMAIL],
  itemLabel: "Customer email",
  itemLabelPlural: "Customer emails",
  displayName: "Customer email",
  emptyValueText: "Enter email",
  hiddenFromPicker: true,
};

export const FailedRmaCreationFilter: BooleanTableFilter = {
  type: AdvancedFilterType.BOOLEAN,
  Icon: StatusIcon,
  operators: [BooleanFilterOperator.IS],
  data: returnTableFilterData[ReturnTableFilterNames.FAILED_RMA_CREATION],
  boolDisplay: { trueDisplay: "true", falseDisplay: "false" },
  displayName: "Failed RMA creation",
};

export const DropoffStatusAdvancedFilter: ArrayStringTableFilter<DropoffStatus> =
  {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: MarkerPinIcon,
    operators: [ArrayStringFilterOperator.ANY_OF],
    data: returnTableFilterData[ReturnTableFilterNames.DROPOFF_STATUS],
    itemLabel: "drop-off status",
    itemLabelPlural: "drop-off statuses",
    values: Object.values(DropoffStatus),
    display: (item: DropoffStatus) => dropoffStatusToDisplayText[item] || item,
    displayName: "Drop-off status",
  };

export const ProvisioningFilter: BooleanTableFilter = {
  type: AdvancedFilterType.BOOLEAN,
  Icon: RefreshIcon,
  operators: [BooleanFilterOperator.IS],
  data: returnTableFilterData[ReturnTableFilterNames.PROVISIONING],
  boolDisplay: { trueDisplay: "instant", falseDisplay: "standard" },
  displayName: "Compensation issued",
};

export const ShippingCountryAdvancedFilter: ArrayStringTableFilter<string> = {
  type: AdvancedFilterType.ARRAY_TO_STRING,
  Icon: Globe02Icon,
  operators: [
    ArrayStringFilterOperator.ANY_OF,
    ArrayStringFilterOperator.NONE_OF,
  ],
  data: returnTableFilterData[ReturnTableFilterNames.SHIPPING_COUNTRY],
  itemLabel: "country",
  itemLabelPlural: "countries",
  values: [...COUNTRY_CODES],
  display: (code: string) => code,
  getValueSummary: (values: string[]) => values.join(", "),
  displayName: "Customer country",
  shortcuts: [EUROPEAN_UNION_SHORTCUT],
};

export const returnsCenterNameCache = new Map<string, string>();

/**
 * Values are returns center ids (matching returns_center_id in OpenSearch);
 * names are fetched lazily when the dropdown opens.
 */
export function createReturnsCenterAdvancedFilter(
  managedReturnsRpcClient?: RedoManagedReturnsRpcClient,
): ArrayStringTableFilter<string> {
  return {
    type: AdvancedFilterType.ARRAY_TO_STRING,
    Icon: Building02Icon,
    operators: [
      ArrayStringFilterOperator.ANY_OF,
      ArrayStringFilterOperator.NONE_OF,
    ],
    data: returnTableFilterData[ReturnTableFilterNames.RETURNS_CENTER],
    itemLabel: "returns center",
    itemLabelPlural: "returns centers",
    displayName: "Returns center",
    dbSearch: true,
    valueFetcher: managedReturnsRpcClient
      ? async ({ filterSearch }, signal) => {
          try {
            const { returnsCenters } =
              await managedReturnsRpcClient.getReturnsCenters({}, { signal });
            for (const center of returnsCenters) {
              returnsCenterNameCache.set(center.id, center.name);
            }
            let centers = returnsCenters;
            if (filterSearch) {
              centers = centers.filter((center) =>
                center.name.toLowerCase().includes(filterSearch.toLowerCase()),
              );
            }
            return centers.map((center) => center.id);
          } catch (error) {
            console.error("Failed to fetch returns centers:", error);
            return [];
          }
        }
      : undefined,
    display: (id: string) => returnsCenterNameCache.get(id) ?? id,
  };
}

function createLocalizedExchangePaymentStatusFilter(): ArrayStringTableFilter<ExchangePaymentStatus> {
  return {
    ...ExchangePaymentStatusAdvancedFilter,
    display: (item: ExchangePaymentStatus) =>
      item === ExchangePaymentStatus.PENDING
        ? t("Pending")
        : t("Complete", { context: "payment status" }),
    displayName: t("Exchange payment status"),
    itemLabel: t("payment status", { context: "filter value noun" }),
    itemLabelPlural: t("payment statuses", { context: "filter value noun" }),
  };
}

export function createReturnsListAdvancedFilters({
  hasReturnsCenters,
  includeDisposition,
  includeCommerceAgentOrigin = false,
  language,
  managedReturnsRpcClient,
  merchantRpcClient,
}: {
  hasReturnsCenters: boolean;
  includeDisposition: boolean;
  includeCommerceAgentOrigin?: boolean;
  language: string;
  managedReturnsRpcClient?: RedoManagedReturnsRpcClient;
  merchantRpcClient: RedoMerchantRpcClient;
}): AdvancedTableFilter[] {
  const filters: AdvancedTableFilter[] = [
    { ...DateRangeFilter, displayName: t("Return created date") },
    { ...ProcessedDateRangeFilter, displayName: t("Return processed date") },
    {
      ...ZipCodeAdvancedFilter,
      displayName: t("Order ZIP", { context: "postal code" }),
      emptyValueText: t("Enter ZIP", { context: "postal code" }),
      itemLabel: t("Order ZIP", { context: "postal code" }),
      itemLabelPlural: t("Order ZIPs", { context: "postal code" }),
    },
    createLocalizedExchangePaymentStatusFilter(),
    {
      ...ReturnTypeAdvancedFilter,
      display: getReturnListTypeLabel,
      displayName: t("Compensation method", {
        context: "return compensation method",
      }),
      itemLabel: t("type", { context: "filter value noun" }),
      itemLabelPlural: t("types", { context: "filter value noun" }),
    },
    {
      ...ReturnStatusAdvancedFilter,
      display: getReturnListStatusLabel,
      displayName: t("Status"),
      itemLabel: t("status", { context: "filter value noun" }),
      itemLabelPlural: t("statuses", { context: "filter value noun" }),
    },
    {
      ...ShipmentStatusAdvancedFilter,
      display: getReturnListShipmentTrackerLabel,
      displayName: t("Shipment status"),
      itemLabel: t("shipment status", { context: "filter value noun" }),
      itemLabelPlural: t("shipment statuses", { context: "filter value noun" }),
    },
    {
      ...ShipmentDaysSinceLastUpdateFilter,
      displayName: t("Shipment days since last update"),
    },
    {
      ...PartiallyProcessedFilter,
      boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
      displayName: t("Partially processed"),
    },
    {
      ...AwaitingRemitFilter,
      boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
      displayName: t("Awaiting remit", {
        context: "financial remittance after warehouse processing",
      }),
    },
    {
      ...FailedRmaCreationFilter,
      boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
      displayName: t("Failed RMA creation", { context: "RMA acronym" }),
    },
    {
      ...NeedsActionFilter,
      boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
      displayName: t("Needs action"),
    },
    {
      ...CustomerEmailFilter,
      itemLabel: t("Customer email"),
      itemLabelPlural: t("Customer emails"),
      displayName: t("Customer email"),
      emptyValueText: t("Enter email"),
    },
    {
      ...ProvisioningFilter,
      boolDisplay: {
        trueDisplay: t("instant", { context: "return compensation timing" }),
        falseDisplay: t("standard", { context: "return compensation timing" }),
      },
      displayName: t("Compensation issued", {
        context: "return compensation timing",
      }),
    },
    {
      ...DropoffStatusAdvancedFilter,
      display: getReturnListDropoffStatusLabel,
      displayName: t("Drop-off status"),
      itemLabel: t("drop-off status", { context: "filter value noun" }),
      itemLabelPlural: t("drop-off statuses", { context: "filter value noun" }),
    },
    {
      ...ShippingCountryAdvancedFilter,
      displayName: t("Customer country"),
      itemLabel: t("country", { context: "filter value noun" }),
      itemLabelPlural: t("countries", { context: "filter value noun" }),
    },
    {
      ...ProductSkuAdvancedFilter,
      displayName: t("Product SKU"),
      emptyValueText: t("Enter SKU"),
      itemLabel: t("SKU"),
      itemLabelPlural: t("SKUs"),
    },
    {
      ...createTagsAdvancedFilter(merchantRpcClient),
      displayName: t("Tags"),
      itemLabel: t("tag", { context: "filter value noun" }),
      itemLabelPlural: t("tags", { context: "filter value noun" }),
    },
  ];

  if (includeCommerceAgentOrigin) {
    filters.push({
      type: AdvancedFilterType.BOOLEAN,
      Icon: CheckVerified01Icon,
      operators: [BooleanFilterOperator.IS, BooleanFilterOperator.IS_NOT],
      data: returnTableFilterData[
        ReturnTableFilterNames.COMMERCE_AGENT_CREATED
      ],
      displayName: t("Created through Commerce Agent"),
      boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
    });
  }

  if (includeDisposition) {
    filters.push({
      ...DispositionCompletedFilter,
      boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
      displayName: t("Awaiting disposition", {
        context:
          "returned item still waiting for warehouse inspection grade and restock or discard decision not legal or equipment disposition",
      }),
    });
  }
  if (hasReturnsCenters) {
    filters.push({
      ...createReturnsCenterAdvancedFilter(managedReturnsRpcClient),
      displayName: t("Returns center"),
      itemLabel: t("returns center", { context: "filter value noun" }),
      itemLabelPlural: t("returns centers", { context: "filter value noun" }),
    });
  }

  return sortAdvancedFiltersByDisplayName(filters, language);
}

export function createWarrantyListAdvancedFilters({
  language,
  merchantRpcClient,
}: {
  language: string;
  merchantRpcClient: RedoMerchantRpcClient;
}): AdvancedTableFilter[] {
  return sortAdvancedFiltersByDisplayName(
    [
      { ...DateRangeFilter, displayName: t("Return created date") },
      { ...ProcessedDateRangeFilter, displayName: t("Return processed date") },
      createLocalizedExchangePaymentStatusFilter(),
      {
        ...ReturnStatusAdvancedFilter,
        display: getReturnListStatusLabel,
        displayName: t("Status"),
        itemLabel: t("status", { context: "filter value noun" }),
        itemLabelPlural: t("statuses", { context: "filter value noun" }),
      },
      {
        ...WarrantyRepairFilter,
        boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
        displayName: t("Is repair"),
      },
      {
        ...PartiallyProcessedFilter,
        boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
        displayName: t("Partially processed"),
      },
      {
        ...FailedRmaCreationFilter,
        boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
        displayName: t("Failed RMA creation", { context: "RMA acronym" }),
      },
      {
        ...NeedsActionFilter,
        boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
        displayName: t("Needs action"),
      },
      {
        ...CustomerEmailFilter,
        itemLabel: t("Customer email"),
        itemLabelPlural: t("Customer emails"),
        displayName: t("Customer email"),
        emptyValueText: t("Enter email"),
      },
      {
        ...ProductSkuAdvancedFilter,
        displayName: t("Product SKU"),
        emptyValueText: t("Enter SKU"),
        itemLabel: t("SKU"),
        itemLabelPlural: t("SKUs"),
      },
      {
        ...createTagsAdvancedFilter(merchantRpcClient),
        displayName: t("Tags"),
        itemLabel: t("tag", { context: "filter value noun" }),
        itemLabelPlural: t("tags", { context: "filter value noun" }),
      },
    ],
    language,
  );
}

export function createClaimsListAdvancedFilters({
  language,
  merchantRpcClient,
}: {
  language: string;
  merchantRpcClient: RedoMerchantRpcClient;
}): AdvancedTableFilter[] {
  return sortAdvancedFiltersByDisplayName(
    [
      { ...DateRangeFilter, displayName: t("Return created date") },
      { ...ProcessedDateRangeFilter, displayName: t("Return processed date") },
      createLocalizedExchangePaymentStatusFilter(),
      {
        ...ReviewingPartyFilter,
        boolDisplay: {
          trueDisplay: t("Merchant review"),
          falseDisplay: t("Redo managed"),
        },
        displayName: t("Reviewing party"),
      },
      {
        ...ReturnStatusAdvancedFilter,
        display: getReturnListStatusLabel,
        displayName: t("Status"),
        itemLabel: t("status", { context: "filter value noun" }),
        itemLabelPlural: t("statuses", { context: "filter value noun" }),
      },
      {
        ...PartiallyProcessedFilter,
        boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
        displayName: t("Partially processed"),
      },
      {
        ...FailedRmaCreationFilter,
        boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
        displayName: t("Failed RMA creation", { context: "RMA acronym" }),
      },
      {
        ...NeedsActionFilter,
        boolDisplay: { trueDisplay: t("Yes"), falseDisplay: t("No") },
        displayName: t("Needs action"),
      },
      {
        ...CustomerEmailFilter,
        itemLabel: t("Customer email"),
        itemLabelPlural: t("Customer emails"),
        displayName: t("Customer email"),
        emptyValueText: t("Enter email"),
      },
      {
        ...ProductSkuAdvancedFilter,
        displayName: t("Product SKU"),
        emptyValueText: t("Enter SKU"),
        itemLabel: t("SKU"),
        itemLabelPlural: t("SKUs"),
      },
      {
        ...createTagsAdvancedFilter(merchantRpcClient),
        displayName: t("Tags"),
        itemLabel: t("tag", { context: "filter value noun" }),
        itemLabelPlural: t("tags", { context: "filter value noun" }),
      },
    ],
    language,
  );
}

export function areBothShipmentFiltersActive(
  displayedAdvancedFilters: AdvancedTableFilter[],
): boolean {
  const shipmentStatusFilter = displayedAdvancedFilters.find(
    (filter) => filter.data.name === ReturnTableFilterNames.SHIPMENT_STATUS,
  );
  const shipmentDaysFilter = displayedAdvancedFilters.find(
    (filter) =>
      filter.data.name ===
      ReturnTableFilterNames.SHIPMENT_DAYS_SINCE_LAST_UPDATE,
  );

  return !!(shipmentStatusFilter && shipmentDaysFilter);
}
